import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./CenterAiWriting.css";

export type CenterAiSection = {
  key:string;
  title:string;
  body:string;
};

type AiAction = "DRAFT"|"POLISH"|"SHORTEN"|"OFFICIAL_TONE"|"TEACHER_TONE"|"EVIDENCE_FOCUS";
type AiResult = {
  model?:string;
  sections?:CenterAiSection[];
  warnings?:string[];
  message?:string;
};

function useAiStatus(client:PlatformClient){
  const [available,setAvailable]=useState<boolean|null>(null);
  const [model,setModel]=useState("");
  useEffect(()=>{
    let active=true;
    client.functions.invoke("center-ai-writing",{body:{action:"STATUS"}}).then(({data,error})=>{
      if(!active)return;
      if(error){setAvailable(false);return;}
      const result=(data || {}) as {configured?:boolean;model?:string|null};
      setAvailable(Boolean(result.configured));
      setModel(typeof result.model==="string"?result.model:"");
    }).catch(()=>{if(active)setAvailable(false);});
    return()=>{active=false;};
  },[client]);
  return {available,model};
}

function copyText(value:string){
  return navigator.clipboard.writeText(value);
}

function sectionsText(title:string,sections:CenterAiSection[]){
  return [title,"",...sections.flatMap((section)=>[section.title,section.body,""])].join("\n").trim();
}

function AiStatus({available,model}:{available:boolean|null;model:string}){
  return (
    <div className="center-ai-status">
      <span className={available?"ready":""}>{available===null?"AI 상태 확인 중":available?"AI 연결됨":"AI 연결 준비 중"}</span>
      {model && <small>{model}</small>}
    </div>
  );
}

export function CenterStudentRecordAiPanel({
  client,schoolId,learnerId
}:{
  client:PlatformClient;
  schoolId:string;
  learnerId:string;
}){
  const {available,model}=useAiStatus(client);
  const [sections,setSections]=useState<CenterAiSection[]>([]);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const [warnings,setWarnings]=useState<string[]>([]);

  async function run(action:AiAction){
    setBusy(action);setError("");setNotice("");setWarnings([]);
    const {data,error}=await client.functions.invoke("center-ai-writing",{
      body:{scope:"STUDENT_RECORD",action,schoolId,learnerId,sections}
    });
    setBusy("");
    if(error){setError("AI 학생기록부를 작성하지 못했습니다. AI 연결 설정을 확인해 주세요.");return;}
    const result=(data || {}) as AiResult;
    if(!Array.isArray(result.sections)){setError(result.message || "AI 결과를 불러오지 못했습니다.");return;}
    setSections(result.sections);
    setWarnings(result.warnings || []);
    setNotice(action==="DRAFT"?"AI 초안을 만들었습니다. 교사가 사실과 표현을 확인해 주세요.":"AI 수정안을 반영했습니다. 저장·전송 전 최종 확인해 주세요.");
  }

  const text=useMemo(()=>sectionsText("학생 진로성장 기록부 초안",sections),[sections]);

  return (
    <section className="center-ai-card student-record-ai">
      <header className="center-ai-head">
        <div className="center-ai-title">
          <span className="center-ai-mark">AI</span>
          <div><p className="eyebrow">CENTER AI WRITING</p><h3>AI 학생 진로기록부</h3><p>Student 360·상담·활동·성취 기록을 근거로 교사용 초안을 만듭니다.</p></div>
        </div>
        <AiStatus available={available} model={model}/>
      </header>

      <div className="center-ai-principle">
        <strong>교사 최종확인 필수</strong>
        <span>학교생활기록부 공식 기재를 자동 확정하는 기능이 아닙니다. 학생을 평가·서열화하지 않고 확인된 기록만 요약합니다.</span>
      </div>

      {!sections.length ? (
        <div className="center-ai-empty">
          <div><strong>한 번에 초안 만들기</strong><span>진로성장 요약 · 주요 활동 · 상담관찰 · 역량/성취 · 다음 지원 방향</span></div>
          <button className="center-ai-primary" disabled={available!==true || Boolean(busy)} onClick={()=>void run("DRAFT")}>
            {busy?"AI 작성 중…":available===false?"AI 연결 준비 중":"✦ AI 기록부 초안 만들기"}
          </button>
        </div>
      ):(
        <>
          <div className="center-ai-toolbar">
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("TEACHER_TONE")}>교사 문체</button>
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("EVIDENCE_FOCUS")}>근거 중심</button>
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("SHORTEN")}>간결하게</button>
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("POLISH")}>전체 다듬기</button>
            <button onClick={()=>void copyText(text)}>전체 복사</button>
          </div>
          <div className="center-ai-section-list">
            {sections.map((section,index)=>(
              <article key={section.key}>
                <input value={section.title} onChange={(e)=>setSections((current)=>current.map((item,i)=>i===index?{...item,title:e.target.value}:item))}/>
                <textarea rows={Math.max(4,Math.min(10,section.body.split("\n").length+2))} value={section.body} onChange={(e)=>setSections((current)=>current.map((item,i)=>i===index?{...item,body:e.target.value}:item))}/>
              </article>
            ))}
          </div>
        </>
      )}

      {busy && <p className="center-ai-working"><i/>학생 기록을 근거로 문장을 정리하는 중입니다…</p>}
      {error && <p className="error">{error}</p>}
      {notice && <p className="teacher-success">{notice}</p>}
      {warnings.length>0 && <div className="center-ai-warnings"><strong>확인 메모</strong>{warnings.map((item,index)=><span key={index}>{item}</span>)}</div>}
    </section>
  );
}

export function CenterReportAiPanel({
  client,schoolId,academicYearId,templateVersionId,value,onChange
}:{
  client:PlatformClient;
  schoolId:string;
  academicYearId:string;
  templateVersionId:string;
  value:CenterAiSection[];
  onChange:(sections:CenterAiSection[])=>void;
}){
  const {available,model}=useAiStatus(client);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const [warnings,setWarnings]=useState<string[]>([]);

  async function run(action:AiAction){
    if(!academicYearId)return;
    setBusy(action);setError("");setNotice("");setWarnings([]);
    const {data,error}=await client.functions.invoke("center-ai-writing",{
      body:{scope:"SCHOOL_REPORT",action,schoolId,academicYearId,templateVersionId:templateVersionId||null,sections:value}
    });
    setBusy("");
    if(error){setError("AI 보고서 서술문을 작성하지 못했습니다. AI 연결 설정을 확인해 주세요.");return;}
    const result=(data || {}) as AiResult;
    if(!Array.isArray(result.sections)){setError(result.message || "AI 결과를 불러오지 못했습니다.");return;}
    onChange(result.sections);
    setWarnings(result.warnings || []);
    setNotice(action==="DRAFT"?"운영 데이터를 바탕으로 AI 서술 초안을 만들었습니다.":"AI 수정안을 반영했습니다.");
  }

  const text=useMemo(()=>sectionsText("진로교육 운영보고서 AI 서술 초안",value),[value]);

  return (
    <section className="center-ai-card report-ai report-no-print">
      <header className="center-ai-head">
        <div className="center-ai-title">
          <span className="center-ai-mark">AI</span>
          <div><p className="eyebrow">REPORT AI WRITING</p><h3>AI 보고서 서술문</h3><p>현재 학년도 운영 통계와 프로그램·배지·데이터 품질을 읽어 서술형 초안을 만듭니다.</p></div>
        </div>
        <AiStatus available={available} model={model}/>
      </header>

      {!value.length ? (
        <div className="center-ai-empty">
          <div><strong>보고서 숫자를 문장으로</strong><span>운영총평 · 학년별 해석 · 프로그램 · 배지 · 품질점검 · 다음 과제</span></div>
          <button className="center-ai-primary" disabled={available!==true || Boolean(busy) || !academicYearId} onClick={()=>void run("DRAFT")}>
            {busy?"AI 작성 중…":available===false?"AI 연결 준비 중":"✦ AI 보고서 초안 만들기"}
          </button>
        </div>
      ):(
        <>
          <div className="center-ai-toolbar">
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("OFFICIAL_TONE")}>공식 보고서 문체</button>
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("EVIDENCE_FOCUS")}>근거·수치 강조</button>
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("SHORTEN")}>요약</button>
            <button disabled={Boolean(busy)||available!==true} onClick={()=>void run("POLISH")}>전체 다듬기</button>
            <button onClick={()=>void copyText(text)}>전체 복사</button>
          </div>
          <div className="center-ai-report-grid">
            {value.map((section,index)=>(
              <article key={section.key}>
                <strong>{section.title}</strong>
                <textarea rows={5} value={section.body} onChange={(e)=>onChange(value.map((item,i)=>i===index?{...item,body:e.target.value}:item))}/>
              </article>
            ))}
          </div>
        </>
      )}
      {busy && <p className="center-ai-working"><i/>운영 데이터를 분석해 서술문을 작성하는 중입니다…</p>}
      {error && <p className="error">{error}</p>}
      {notice && <p className="teacher-success">{notice}</p>}
      {warnings.length>0 && <div className="center-ai-warnings"><strong>확인 메모</strong>{warnings.map((item,index)=><span key={index}>{item}</span>)}</div>}
    </section>
  );
}

export function CenterCounselingAiActions({
  client,schoolId,learnerId,topic,notes,nextAction,onApply
}:{
  client:PlatformClient;
  schoolId:string;
  learnerId:string;
  topic:string;
  notes:string;
  nextAction:string;
  onApply:(next:{notes:string;nextAction:string})=>void;
}){
  const {available}=useAiStatus(client);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");

  async function run(action:"DRAFT"|"POLISH"|"SHORTEN"){
    if(!learnerId || !topic.trim())return;
    setBusy(action);setError("");
    const sections:CenterAiSection[]=[
      {key:"notes",title:"상담 기록",body:notes},
      {key:"next-action",title:"다음 조치",body:nextAction}
    ];
    const {data,error}=await client.functions.invoke("center-ai-writing",{
      body:{scope:"COUNSELING",action,schoolId,learnerId,topic,notes,nextAction,sections}
    });
    setBusy("");
    if(error){setError("AI 상담기록 정리를 사용할 수 없습니다.");return;}
    const result=(data || {}) as AiResult;
    if(!Array.isArray(result.sections))return;
    const note=result.sections.find((item)=>item.key==="notes")?.body || notes;
    const actionText=result.sections.find((item)=>item.key==="next-action")?.body || nextAction;
    onApply({notes:note,nextAction:actionText});
  }

  return (
    <div className="center-counseling-ai">
      <div><span className="center-ai-mark">AI</span><strong>상담기록 정리</strong><small>학생 기록과 현재 메모를 바탕으로 교사용 문장으로 정돈합니다.</small></div>
      <div>
        <button disabled={available!==true||Boolean(busy)||!topic.trim()} onClick={()=>void run(notes.trim()?"POLISH":"DRAFT")}>{busy?"AI 정리 중…":"AI로 정리"}</button>
        <button disabled={available!==true||Boolean(busy)||!notes.trim()} onClick={()=>void run("SHORTEN")}>짧게</button>
      </div>
      {error && <small className="error">{error}</small>}
    </div>
  );
}
